import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { hubApi } from '../lib/hubApi';
import type { Severity } from '../components/shared/SeverityBar';

export type HubFinding = {
  id: string;
  severity: Severity;
  category: string;
  title: string;
  context?: string;
  detectedAt: number;
  source: string;
  status: 'open' | 'acknowledged' | 'dismissed' | 'snoozed' | 'bpx_in_progress';
  snoozeUntil: number | null;
  caseRef: string | null;
};

type State = {
  findings: HubFinding[];
  loadedForOrgId: string | null;
  loading: boolean;
  error: string | null;
  loadFindings: (sfOrgId?: string | null) => Promise<void>;
  setStatus: (
    id: string,
    status: HubFinding['status'],
    extra?: { snoozeUntil?: number | null; bpxRequestId?: string | null },
  ) => Promise<void>;
};

export const useHubStore = create<State>()(
  persist(
    (set, get) => ({
      findings: [],
      loadedForOrgId: null,
      loading: false,
      error: null,
      loadFindings: async (sfOrgId) => {
        set({ loading: true, error: null });
        try {
          const { data } = await hubApi.get<{ findings: HubFinding[] }>('/findings');
          set({ findings: data.findings, loading: false, loadedForOrgId: sfOrgId ?? null });
        } catch (err) {
          set({ loading: false, error: (err as Error).message });
        }
      },
      setStatus: async (id, status, extra) => {
        // Snapshot state before the POST so we can roll back on failure.
        const prev = get().findings;
        const optimistic = prev.map((f) =>
          f.id === id
            ? {
                ...f,
                status,
                snoozeUntil: extra?.snoozeUntil ?? null,
                caseRef: extra?.bpxRequestId ?? f.caseRef,
              }
            : f,
        );
        set({ findings: optimistic });
        try {
          await hubApi.post(`/findings/${id}/status`, {
            status,
            snoozeUntil: extra?.snoozeUntil ?? null,
            bpxRequestId: extra?.bpxRequestId ?? null,
          });
        } catch (err) {
          // Server rejected the update — restore previous state so local
          // and server stay in sync, then propagate so callers can toast.
          set({ findings: prev });
          throw err;
        }
      },
    }),
    { name: 'orgiq-hub', storage: createJSONStorage(() => sessionStorage) },
  ),
);
